/* eslint-disable react/no-danger -- Event description HTML is allow-list sanitized by the API before serialization. */
import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CalendarPlus, Mail, MapPin } from 'lucide-react'
import {
  PublicEventSales,
  type PublicEventSalesEvent,
} from '@/components/events/public-event-sales'
import { Alert } from '@/components/ui/alert'
import { type VerifiedEventBuyer } from '@/components/events/member-ticket-verification'
import { Button } from '@/components/ui/button'
import { getJSON } from '@/lib/api'
import type { Association } from '@/pages/StaffSignInPage'

interface PublicEvent extends PublicEventSalesEvent {
  date: string
  time: string
  calendar_url: string
  description: string | null
  event_contact: { name: string; email: string } | null
  hero_image: { filename: string; content_type: string; url: string } | null
  member_session_verified?: boolean
}

interface PublicEventResponse {
  event: PublicEvent
}

export function PublicEventPage({
  association,
  eventId,
}: {
  association: Association
  eventId: number
}) {
  const [memberVerification, setMemberVerification] = React.useState<VerifiedEventBuyer | null>(
    null,
  )
  const eventQuery = useQuery({
    queryKey: ['public-event', eventId, memberVerification?.token],
    queryFn: () =>
      getJSON<PublicEventResponse>(
        `/api/v1/public/events/${eventId}${memberVerification ? `?member_verification_token=${encodeURIComponent(memberVerification.token)}` : ''}`,
      ),
    retry: false,
  })

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex h-14 max-w-6xl items-center px-5 text-body-sm font-medium">
          {association.name}
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-8">
        {eventQuery.isLoading ? (
          <p className="text-body-sm text-muted-foreground" role="status">
            Loading event…
          </p>
        ) : eventQuery.isError || !eventQuery.data ? (
          <Alert>Event unavailable.</Alert>
        ) : (
          <PublicEventContent
            event={eventQuery.data.event}
            associationName={association.name}
            memberVerification={memberVerification}
            onMemberVerification={setMemberVerification}
          />
        )}
      </div>
    </main>
  )
}

function PublicEventContent({
  event,
  associationName,
  memberVerification,
  onMemberVerification,
}: {
  event: PublicEvent
  associationName: string
  memberVerification: VerifiedEventBuyer | null
  onMemberVerification: (verification: VerifiedEventBuyer) => void
}) {
  const [checkoutActive, setCheckoutActive] = React.useState(
    new URLSearchParams(window.location.search).has('event_checkout_session'),
  )
  return (
    <div className="space-y-8">
      {event.cancelled && (
        <div
          role="status"
          className="rounded-md border border-border bg-background px-3 py-2 text-body-sm text-muted-foreground"
        >
          This event was cancelled by {associationName}.
        </div>
      )}
      {event.hero_image && !checkoutActive && (
        <img
          src={event.hero_image.url}
          alt={`${event.name} event hero`}
          className="aspect-[3/1] w-full rounded object-cover"
        />
      )}
      <div className={event.cancelled ? 'max-w-2xl' : 'grid gap-10 lg:grid-cols-[1fr_380px]'}>
        {!checkoutActive && (
          <section className="space-y-4">
            <h1 className="text-heading-lg font-normal tracking-tight">{event.name}</h1>
            <div className="space-y-1.5 text-caption text-muted-foreground">
              <p className="flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                {event.schedule} {event.time_zone_label}
              </p>
              {event.location && (
                <p className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  {event.location}
                </p>
              )}
              {event.event_contact && (
                <p className="flex items-start gap-2">
                  <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <a href={`mailto:${event.event_contact.email}`} className="hover:text-foreground">
                    Questions? {event.event_contact.name} · {event.event_contact.email}
                  </a>
                </p>
              )}
            </div>
            {!event.cancelled && (
              <Button asChild type="button" variant="outline" className="h-8 gap-1.5 px-3">
                <a href={event.calendar_url} download>
                  <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add to calendar
                </a>
              </Button>
            )}
            {event.description && (
              <div
                className="max-w-2xl space-y-3 text-body-sm leading-relaxed text-muted-foreground [&_a]:text-primary [&_li]:ml-5 [&_li]:list-disc [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            )}
          </section>
        )}
        <PublicEventSales
          event={event}
          onCheckoutChange={setCheckoutActive}
          memberVerification={memberVerification}
          memberSessionVerified={event.member_session_verified}
          onMemberVerification={onMemberVerification}
        />
      </div>
    </div>
  )
}
